import React, { forwardRef } from 'react'

import { InputBaseComponentProps, Box, FormHelperText } from '@mui/material'
import { IMaskInput } from 'react-imask'
import { PhoneInput } from 'react-international-phone'
// eslint-disable-next-line import/no-unresolved
import 'react-international-phone/style.css'

const phoneMask = '(#00) 000-0000'

type PhoneNumberMaskedInputProps = InputBaseComponentProps & {
  name: string
  onChange: (event: { target: { name: string; value: string } }) => void
}

export const PhoneNumberMaskedInput = forwardRef<
  HTMLElement,
  PhoneNumberMaskedInputProps
>(function PhoneNumberMasked(props, ref) {
  const { onChange, ...rest } = props

  return (
    <IMaskInput
      {...rest}
      lazy={true}
      mask={phoneMask}
      definitions={{
        '#': /[1-9]/,
      }}
      onAccept={(value: string) => {
        const numericValue = value.replace(/\D/g, '');
        onChange?.({ target: { name: props.name, value: numericValue } });
      }}
      overwrite
      inputRef={ref}
    />
  )
})

export interface PhoneNumberInputProps {
  value: string
  onChange: (val: string) => void
  error?: boolean
  helperText?: string
  placeholder?: string
  disabled?: boolean
  endAdornment?: React.ReactNode
  context?: 'onboarding' | 'dashboard' | 'postSubscription'
}

export const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  error = false,
  helperText = '',
  placeholder = '',
  disabled = false,
  endAdornment,
  context = 'other'
}) => {
  const darkTheme = context === 'onboarding'
  const isInitialMount = React.useRef(true)

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      isInitialMount.current = false
    }, 100)
    return () => clearTimeout(timeout)
  }, [])

  const handleChange = (val: string) => {
    if (isInitialMount.current && (!value || value === '') && val.startsWith('+') && val.length <= 4) {
      return
    }
    onChange(val)
  }

  const bgColor = 'transparent'
  const dropdownBgColor = darkTheme ? '#222329' : '#FFFFFF'
  const textColor = disabled && !darkTheme 
    ? 'rgba(0, 0, 0, 0.38)' 
    : (darkTheme ? '#EFEDFDB2' : '#000000')
  const borderColor = darkTheme 
    ? (error ? '#f44336' : '#EFEDFDB2') 
    : (error ? '#ff3d00' : (disabled ? '#E2E8F0' : '#9135E0'))
  
  const dropdownBorderColor = darkTheme ? 'rgba(255, 255, 255, 0.1)' : '#EFEDFDB2'
  const scrollbarThumbColor = darkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
  const scrollbarThumbHoverColor = darkTheme ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'
  const itemHoverColor = darkTheme ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'
  const itemSelectedColor = darkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
  const itemSelectedHoverColor = darkTheme ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'

  const inputHeight = darkTheme ? '50px' : '50px'
  const borderRadius = darkTheme ? '10px' : '10px'

  return (
    <Box sx={{
      width: '100%',
      position: 'relative',
      '& .react-international-phone-input-container': {
        position: 'relative !important',
        gap: '0 !important',
      },
      '& .react-international-phone-country-selector': {
        position: 'static !important',
      },
      '& .react-international-phone-country-selector-dropdown': {
        width: '100% !important',
        backgroundColor: dropdownBgColor,
        color: textColor,
        marginTop: '4px',
        borderRadius: '4px',
        maxHeight: '300px !important',
        border: `1px solid ${dropdownBorderColor}`,
        outline: 'none',
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: dropdownBgColor,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: dropdownBgColor,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: scrollbarThumbColor,
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: scrollbarThumbHoverColor,
        },
      },
      '& .react-international-phone-country-selector-dropdown__list-item': {
        fontFamily: darkTheme ? 'InterMedium !important' : 'InterRegular !important',
        fontSize: '14px !important',
        color: `${textColor} !important`,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: `${itemHoverColor} !important`,
        },
        '&[aria-selected="true"]': {
          backgroundColor: `${itemSelectedColor} !important`,
          '&:hover': {
            backgroundColor: `${itemSelectedHoverColor} !important`,
          }
        }
      }
    }}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <PhoneInput
          disabled={disabled}
        value={value}
        onChange={handleChange}
        defaultCountry="us"
        style={{ display: 'flex', width: '100%' }}
        inputProps={{
          placeholder: placeholder,
        }}
        inputStyle={{ 
          flex: 1,
          width: '100%', 
          height: inputHeight,
          boxSizing: 'border-box',
          padding: '8.5px 14px',
          fontSize: '14px',
          fontFamily: darkTheme ? 'InterMedium' : 'InterRegular',
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}`,
          borderLeft: 'none',
          borderTopRightRadius: borderRadius,
          borderBottomRightRadius: borderRadius,
          color: textColor,
          opacity: (disabled && darkTheme) ? 0.5 : 1
        }}
        countrySelectorStyleProps={{
          buttonStyle: {
            height: inputHeight,
            boxSizing: 'border-box',
            backgroundColor: bgColor,
            border: `1px solid ${borderColor}`,
            borderTopLeftRadius: borderRadius,
            borderBottomLeftRadius: borderRadius,
            padding: '0 10px',
            color: textColor,
            opacity: (disabled && darkTheme) ? 0.5 : 1
          },
          dropdownStyleProps: {
            style: {
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '100%',
              zIndex: 1300,
              backgroundColor: dropdownBgColor,
              color: textColor,
              border: `1px solid ${dropdownBorderColor}`
            }
          }
        }}
      />
      {endAdornment && (
        <Box sx={{
          position: 'absolute',
          right: '14px',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          pointerEvents: disabled ? 'none' : 'auto'
        }}>
          {endAdornment}
        </Box>
      )}
      </Box>
      {helperText && (
        <FormHelperText error={error} sx={{ 
          marginLeft: 0, 
          marginRight: 0,
          fontFamily: darkTheme ? 'InterMedium' : 'InterSemiBold',
          fontSize: '14px',
          lineHeight: '17px',
          fontWeight: 400,
          paddingLeft: context === 'onboarding' ? '0' : '14px',
          ...(error ? { color: darkTheme ? '#f44336' : 'rgb(255, 61, 0) !important' } : {})
        }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  )
}
